'use client';

import { UserIcon, XIcon } from 'lucide-react';
import Image from 'next/image';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/auth-context';
import { formatTimeAgo, getUserInitials } from '@/lib/utils';
import { Notification } from '@/types/notifications';

const highlightMentions = (text: string, username: string) => {
  // Regex to match @username (case-insensitive)
  const regex = new RegExp(`@${username}`, 'gi');
  const parts = text.split(regex);

  // If no match, return as is
  if (parts.length === 1) return text;

  // Rebuild with highlights
  const result: React.ReactNode[] = [];
  parts.forEach((part, i) => {
    result.push(part);
    if (i < parts.length - 1) {
      result.push(
        <span key={i} className='font-semibold text-brand-green'>
          @{username}
        </span>,
      );
    }
  });
  return result;
};

type NotificationItemProps = {
  notification: Notification;
  onDelete: (notification: Notification) => void;
};

export function NotificationItem({
  notification,
  onDelete,
}: NotificationItemProps) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  let avatar: React.ReactNode = null;
  let title: React.ReactNode = '';
  let content: React.ReactNode = '';

  switch (notification.type) {
    case 'message_new':
      avatar = (
        <Avatar>
          <AvatarImage
            width={40}
            height={40}
            src={notification.sender?.profilePicture}
            referrerPolicy='no-referrer'
          />
          <AvatarFallback className='bg-gray-100 text-sm font-medium'>
            {notification.sender?.username ? (
              getUserInitials(notification.sender?.username, 2)
            ) : (
              <UserIcon width={40} height={40} />
            )}
          </AvatarFallback>
        </Avatar>
      );
      title = notification.sender?.username || 'User';
      content = (
        <>
          Has sent you a message
          <p className='mt-1 line-clamp-3 border-l-4 border-brand-green pl-2'>
            {notification.message}
          </p>
        </>
      );
      break;
    case 'message_reply':
      avatar = (
        <Avatar>
          <AvatarImage
            width={40}
            height={40}
            src={notification.sender?.profilePicture}
            referrerPolicy='no-referrer'
          />
          <AvatarFallback className='bg-gray-100 text-sm font-medium'>
            {notification.sender?.username ? (
              getUserInitials(notification.sender?.username, 2)
            ) : (
              <UserIcon width={40} height={40} />
            )}
          </AvatarFallback>
        </Avatar>
      );
      title = notification.sender?.username || 'User';
      content = (
        <>
          Replied to your message
          <p className='mt-1 line-clamp-3 border-l-4 border-brand-green pl-2'>
            {highlightMentions(
              notification.message || '',
              user?.username || '',
            )}
          </p>
        </>
      );
      break;
    case 'message_reminder':
    case 'unread_message':
      avatar = (
        <Avatar>
          <AvatarImage
            width={40}
            height={40}
            src={notification.sender?.profilePicture}
            referrerPolicy='no-referrer'
          />
          <AvatarFallback className='bg-gray-100 text-sm font-medium'>
            {notification.sender?.username ? (
              getUserInitials(notification.sender?.username, 2)
            ) : (
              <UserIcon width={40} height={40} />
            )}
          </AvatarFallback>
        </Avatar>
      );
      title = notification.sender?.username || 'Skill4Skill';
      content = (
        <>
          Message reminder
          <p className='mt-1 line-clamp-3 border-l-4 border-brand-green pl-2'>
            {highlightMentions(
              notification.message || 'You have unread messages.',
              user?.username || '',
            )}
          </p>
        </>
      );
      break;
    case 'mention_post':
      avatar = (
        <Avatar>
          <AvatarImage
            width={40}
            height={40}
            src={notification.sender?.profilePicture}
            referrerPolicy='no-referrer'
          />
          <AvatarFallback className='bg-gray-100 text-sm font-medium'>
            {notification.sender?.username ? (
              getUserInitials(notification.sender?.username, 2)
            ) : (
              <UserIcon width={40} height={40} />
            )}
          </AvatarFallback>
        </Avatar>
      );
      title = notification.sender?.username || 'User';
      content = (
        <>
          Mentioned you in a post
          <p className='mt-1 line-clamp-3 border-l-4 border-brand-green pl-2'>
            {highlightMentions(
              notification.message || '',
              user?.username || '',
            )}
          </p>
        </>
      );
      break;
    case 'review_received':
      avatar = (
        <Avatar>
          <AvatarImage
            width={40}
            height={40}
            src={notification.sender?.profilePicture}
            referrerPolicy='no-referrer'
          />
          <AvatarFallback className='bg-gray-100 text-sm font-medium'>
            {notification.sender?.username ? (
              getUserInitials(notification.sender?.username, 2)
            ) : (
              <UserIcon width={40} height={40} />
            )}
          </AvatarFallback>
        </Avatar>
      );
      title = notification.sender?.username || 'User';
      content = (
        <>
          Left you a new review
          <p className='mt-1 line-clamp-3 border-l-4 border-brand-green pl-2'>
            {notification.message || 'You received a new review.'}
          </p>
        </>
      );
      break;
    case 'system_alert':
      avatar = (
        <div className='flex size-10 items-center justify-center rounded-full'>
          <Image
            src='/logos/skill4skill-logo-transparent.png'
            alt='Skill4Skill Logo'
            width={40}
            height={40}
            className='object-cover'
          />
        </div>
      );
      title = 'Skill4Skill Team';
      content = (
        <>
          <p>
            {highlightMentions(
              notification.message || '',
              user?.username || '',
            )}
          </p>
        </>
      );
      break;
    case 'service_request_received':
      avatar = (
        <Avatar>
          <AvatarImage
            width={40}
            height={40}
            src={notification.sender?.profilePicture}
            referrerPolicy='no-referrer'
          />
          <AvatarFallback className='bg-gray-100 text-sm font-medium'>
            {notification.sender?.username ? (
              getUserInitials(notification.sender?.username, 2)
            ) : (
              <UserIcon width={40} height={40} />
            )}
          </AvatarFallback>
        </Avatar>
      );
      title = notification.sender?.username || 'User';
      content = (
        <>
          {notification.message ? (
            <>
              {notification.message.startsWith('Service request:')
                ? 'Sent you a service request'
                : 'Sent you a service offer'}
              <p className='mt-1 line-clamp-3 border-l-4 border-brand-green pl-2'>
                {notification.message}
              </p>
            </>
          ) : (
            'Has sent you a service request'
          )}
        </>
      );
      break;
    case 'service_request_accepted':
      avatar = (
        <Avatar>
          <AvatarImage
            width={40}
            height={40}
            src={notification.sender?.profilePicture}
            referrerPolicy='no-referrer'
          />
          <AvatarFallback className='bg-gray-100 text-sm font-medium'>
            {notification.sender?.username ? (
              getUserInitials(notification.sender?.username, 2)
            ) : (
              <UserIcon width={40} height={40} />
            )}
          </AvatarFallback>
        </Avatar>
      );
      title = notification.sender?.username || 'User';
      content = (
        <>
          Accepted your service request
          {notification.message && (
            <p className='mt-1 line-clamp-3 border-l-4 border-brand-green pl-2'>
              {notification.message}
            </p>
          )}
        </>
      );
      break;
    case 'service_request_rejected':
      avatar = (
        <Avatar>
          <AvatarImage
            width={40}
            height={40}
            src={notification.sender?.profilePicture}
            referrerPolicy='no-referrer'
          />
          <AvatarFallback className='bg-gray-100 text-sm font-medium'>
            {notification.sender?.username ? (
              getUserInitials(notification.sender?.username, 2)
            ) : (
              <UserIcon width={40} height={40} />
            )}
          </AvatarFallback>
        </Avatar>
      );
      title = notification.sender?.username || 'User';
      content = (
        <>
          Rejected your service request
          {notification.message && (
            <p className='mt-1 line-clamp-3 border-l-4 border-red-500 pl-2'>
              {notification.message}
            </p>
          )}
        </>
      );
      break;
    case 'task_in_progress':
      avatar = (
        <Avatar>
          <AvatarImage
            width={40}
            height={40}
            src={notification.sender?.profilePicture}
            referrerPolicy='no-referrer'
          />
          <AvatarFallback className='bg-gray-100 text-sm font-medium'>
            {notification.sender?.username ? (
              getUserInitials(notification.sender?.username, 2)
            ) : (
              <UserIcon width={40} height={40} />
            )}
          </AvatarFallback>
        </Avatar>
      );
      title = notification.sender?.username || 'User';
      content = (
        <>
          Service update
          {notification.message && (
            <p className='mt-1 line-clamp-3 border-l-4 border-brand-green pl-2'>
              {notification.message}
            </p>
          )}
        </>
      );
      break;
    case 'skillcoin_debited':
      avatar = (
        <Avatar>
          <AvatarImage
            width={40}
            height={40}
            src={notification.sender?.profilePicture}
            referrerPolicy='no-referrer'
          />
          <AvatarFallback className='bg-gray-100 text-sm font-medium'>
            {notification.sender?.username ? (
              getUserInitials(notification.sender?.username, 2)
            ) : (
              <UserIcon width={40} height={40} />
            )}
          </AvatarFallback>
        </Avatar>
      );
      title = notification.sender?.username || 'User';
      content = (
        <>
          SkillCoins debited
          {notification.message && (
            <p className='mt-1 line-clamp-3 border-l-4 border-brand-green pl-2'>
              {notification.message}
            </p>
          )}
        </>
      );
      break;
    case 'skillcoin_credited':
      avatar = (
        <Avatar>
          <AvatarImage
            width={40}
            height={40}
            src={notification.sender?.profilePicture}
            referrerPolicy='no-referrer'
          />
          <AvatarFallback className='bg-gray-100 text-sm font-medium'>
            {notification.sender?.username ? (
              getUserInitials(notification.sender?.username, 2)
            ) : (
              <UserIcon width={40} height={40} />
            )}
          </AvatarFallback>
        </Avatar>
      );
      title = notification.sender?.username || 'User';
      content = (
        <>
          SkillCoins received
          {notification.message && (
            <p className='mt-1 line-clamp-3 border-l-4 border-brand-green pl-2'>
              {notification.message}
            </p>
          )}
        </>
      );
      break;
    case 'service_request_completed':
      avatar = (
        <Avatar>
          <AvatarImage
            width={40}
            height={40}
            src={notification.sender?.profilePicture}
            referrerPolicy='no-referrer'
          />
          <AvatarFallback className='bg-gray-100 text-sm font-medium'>
            {notification.sender?.username ? (
              getUserInitials(notification.sender?.username, 2)
            ) : (
              <UserIcon width={40} height={40} />
            )}
          </AvatarFallback>
        </Avatar>
      );
      title = notification.sender?.username || 'User';
      content = (
        <>
          Marked a service as completed
          {notification.message && (
            <p className='mt-1 line-clamp-3 border-l-4 border-brand-green pl-2'>
              {notification.message}
            </p>
          )}
        </>
      );
      break;
    case 'service_request_confirmed':
      avatar = (
        <Avatar>
          <AvatarImage
            width={40}
            height={40}
            src={notification.sender?.profilePicture}
            referrerPolicy='no-referrer'
          />
          <AvatarFallback className='bg-gray-100 text-sm font-medium'>
            {notification.sender?.username ? (
              getUserInitials(notification.sender?.username, 2)
            ) : (
              <UserIcon width={40} height={40} />
            )}
          </AvatarFallback>
        </Avatar>
      );
      title = notification.sender?.username || 'User';
      content = (
        <>
          Confirmed service completion
          {notification.message && (
            <p className='mt-1 line-clamp-3 border-l-4 border-brand-green pl-2'>
              {notification.message}
            </p>
          )}
        </>
      );
      break;
    case 'service_request_disputed':
      avatar = (
        <Avatar>
          <AvatarImage
            width={40}
            height={40}
            src={notification.sender?.profilePicture}
            referrerPolicy='no-referrer'
          />
          <AvatarFallback className='bg-gray-100 text-sm font-medium'>
            {notification.sender?.username ? (
              getUserInitials(notification.sender?.username, 2)
            ) : (
              <UserIcon width={40} height={40} />
            )}
          </AvatarFallback>
        </Avatar>
      );
      title = notification.sender?.username || 'User';
      content = (
        <>
          Disputed a service
          {notification.message && (
            <p className='mt-1 line-clamp-3 border-l-4 border-red-500 pl-2'>
              {notification.message}
            </p>
          )}
        </>
      );
      break;
    case 'service_request_dispute_resolved':
      avatar = (
        <Avatar>
          <AvatarImage
            width={40}
            height={40}
            src={notification.sender?.profilePicture}
            referrerPolicy='no-referrer'
          />
          <AvatarFallback className='bg-gray-100 text-sm font-medium'>
            {notification.sender?.username ? (
              getUserInitials(notification.sender?.username, 2)
            ) : (
              <UserIcon width={40} height={40} />
            )}
          </AvatarFallback>
        </Avatar>
      );
      title = notification.sender?.username || 'User';
      content = (
        <>
          Dispute resolved
          {notification.message && (
            <p className='mt-1 line-clamp-3 border-l-4 border-yellow-500 pl-2'>
              {notification.message}
            </p>
          )}
        </>
      );
      break;
    default:
      title = 'Notification';
      content = 'You have a new notification.';
  }

  return (
    <>
      <div className='shrink-0'>{avatar}</div>
      <div className='min-w-0 flex-1 text-sm font-medium text-subtext'>
        <div className='font-semibold text-header'>{title}</div>
        <div>{content}</div>
        <div className='mt-1'>{formatTimeAgo(notification.createdAt)}</div>
      </div>
      <Button
        variant='transparent'
        size='icon'
        className='size-6 rounded-full p-0 text-muted-foreground hover:text-destructive'
        aria-label='Delete notification'
        onClick={(e) => {
          e.stopPropagation();
          onDelete(notification);
        }}
      >
        <XIcon />
      </Button>
    </>
  );
}
